import type { ErrorLike } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
} from 'react';

import { useSnackbarError } from '@hooks/useSnackbarErrors';
import { useSyncState } from '@hooks/useSyncState';
import { useBasketContext } from '@providers/basket/BasketProvider/Context';
import { useTrackingContext } from '@tracking/Provider/Context';
import {
  signupCbcCalcStateEntity,
  useTrackSignupCbcCalcSaved,
} from '@tracking/trackingEvents';

import {
  cashbackCalculatorRetailersQuery,
  saveCalculatorDataMutation,
} from '../query';
import {
  type CalculationOutput,
  type CalculatorFields,
  type CashbackCalculatorContextState,
  EarnType,
  SpendType,
} from '../types';
import { CashbackCalculatorContext } from './CalculatorContext';

const outputDefault: CalculationOutput = {
  categories: [],
  total: {
    earnings: 0,
    spend: 0,
  },
};

interface CashbackCalculatorProviderProps {
  children: ReactNode;
  /**
   * accept a ref into the provider directly, which will
   * allow sending `requestSubmit()` from a higher level
   * component, outside the provider. In this case, for example,
   * it could be submitting the cashback page itself which can
   * trigger form submission and send the values back to the
   * provider to be handled.
   */
  formRef?: RefObject<Nullable<HTMLFormElement>>;
  coreServiceCount?: number;
  /**
   * lets a component outside the provider (e.g. the cashback page's remove
   * flow) trigger a save with `useCashbackValueInComms` forced to false,
   * reusing the calculator's own form submit so the current spend data and
   * estimate are persisted alongside it.
   */
  disableCommsRef?: RefObject<Nullable<() => void>>;
}

const CashbackCalculatorProvider = ({
  children,
  formRef: externalFormRef,
  coreServiceCount = 0,
  disableCommsRef,
}: CashbackCalculatorProviderProps) => {
  const { basketId } = useBasketContext();
  const formValuesRef = useRef<CalculatorFields>(null);
  const localFormRef = useRef<HTMLFormElement>(null);
  const formRef = externalFormRef ?? localFormRef;
  const {
    loading: retailersLoading,
    error: retailersError,
    data: retailersData,
  } = useQuery(cashbackCalculatorRetailersQuery, {
    fetchPolicy: 'cache-first',
  });

  const { setSignupCbcCalcStateContext } = useTrackingContext();
  const trackSignupCbcCalcSaved = useTrackSignupCbcCalcSaved();

  const [saveCalculatorData] = useMutation(saveCalculatorDataMutation);

  const handleError = useSnackbarError();

  useEffect(() => {
    if (retailersError && retailersError.name !== 'AbortError') {
      handleError(retailersError);
    }
  }, [retailersError, handleError]);

  const [outputCategory, setOutputCategory] = useSyncState<CalculationOutput>(
    'cbc-calc-output-category',
    outputDefault
  );
  const [outputBrand, setOutputBrand] = useSyncState<CalculationOutput>(
    'cbc-calc-output-brand',
    outputDefault
  );
  const [currentSpendCalc, setCurrentSpendCalc] = useSyncState(
    'cbc-calc-spend-type',
    SpendType.Category
  );

  const [currentEarnTab, setCurrentEarnTab] = useSyncState(
    'cbc-calc-earn-tab',
    EarnType.OverTime
  );

  const [useCashbackValueInComms, setUseCashbackValueInComms] = useSyncState(
    'cbc-calc-include-in-comms',
    false
  );

  const handleSubmitForm = useCallback(
    () => formRef.current?.requestSubmit(),
    [formRef]
  );

  const handleSaveForm = async (values: CalculatorFields) => {
    formValuesRef.current = values;
    const estimatedCashbackValue =
      state.output[currentSpendCalc].total.earnings;
    try {
      const details = {
        data: formValuesRef.current,
        estimatedCashbackValue,
        useCashbackValueInComms,
      };
      setSignupCbcCalcStateContext({
        include_in_bundle: useCashbackValueInComms,
        estimated_cashback_value: estimatedCashbackValue,
      });

      await saveCalculatorData({ variables: { basketId, details } });

      trackSignupCbcCalcSaved(
        { calc_type: currentSpendCalc },
        {
          contexts: [
            signupCbcCalcStateEntity({
              include_in_bundle: useCashbackValueInComms,
              estimated_cashback_value: estimatedCashbackValue,
            }),
          ],
        }
      );
    } catch (error) {
      handleError(error as ErrorLike);
    }
  };

  const handleSetUseCashbackValueInComms = (value: boolean) => {
    setUseCashbackValueInComms(value);
    handleSubmitForm();
  };

  useEffect(() => {
    if (!disableCommsRef) return;
    disableCommsRef.current = () => handleSetUseCashbackValueInComms(false);
    return () => {
      disableCommsRef.current = null;
    };
  });

  const state: CashbackCalculatorContextState = {
    output: {
      [SpendType.Category]: outputCategory,
      [SpendType.Brand]: outputBrand,
    },
    setOutput: (type, output) => {
      if (type === SpendType.Category) {
        setOutputCategory(output);
      }

      if (type === SpendType.Brand) {
        setOutputBrand(output);
      }
    },
    currentSpendCalc,
    setCurrentSpendCalc,
    currentEarnTab,
    setCurrentEarnTab,
    retailers: retailersData?.cashbackCalculatorRetailers ?? {
      partners: [],
      nonPartners: [],
    },
    retailersLoading,
    coreServiceCount,
    useCashbackValueInComms,
    setUseCashbackValueInComms: handleSetUseCashbackValueInComms,
    initialFormValues: {
      cbcCalcSpendBrandSearch: '',
      cbcCalcGroceries: 320,
      cbcCalcTransportFuel: 380,
      cbcCalcFashionBeauty: 150,
      cbcCalcHomeGarden: 240,
      cbcCalcFoodDrink: 150,
      cbcCalcEntertainmentTech: 230,
      cbcCalcDaysOutGiftsHolidays: 330,
      cbcCalcBrandFields: {},
    },
    saveForm: handleSaveForm,
    submitForm: handleSubmitForm,
    formRef,
  };

  return (
    <CashbackCalculatorContext value={state}>
      {children}
    </CashbackCalculatorContext>
  );
};

export { CashbackCalculatorProvider };
